import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CoursesService } from 'src/app/services/courses.service';
import { IAdminQuizQuestionsQueryParams } from './admin-quiz-questions.viewmodel';
import { DatatablesHelper, RouteHelper } from 'src/app/helpers';
import { QuizService } from 'src/app/services/quiz.service';
import { AdminService } from 'src/app/services/admin.service';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminQuizQuestionDialogComponent } from './admin-quiz-question-dialog/admin-quiz-question-dialog.component';
import { FlashMessagesService } from 'flash-messages-angular';
import { AdminQuizQuestionDialogData } from './admin-quiz-question-dialog/admin-quiz-question-dialog.viewmodel';
import { RouteService } from 'src/app/core/services';

@Component({
  selector: 'app-admin-quiz-questions',
  templateUrl: './admin-quiz-questions.component.html',
  styleUrls: ['./admin-quiz-questions.component.css']
})
export class AdminQuizQuestionsComponent implements OnInit {

  // List of courses
  courses: any[] = [];
  questions?: any[];
  loaded = false;

  course: any;
  courseID: string = '';

  module: any;
  moduleID: string = '';

  routeParams: IAdminQuizQuestionsQueryParams = {};

  // Needed  this to display option letters
  letterArray: String[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    private coursesService: CoursesService,
    private quizService: QuizService,
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute,
    private routeService: RouteService,
    private dialog: MatDialog,
    private flashMessage: FlashMessagesService
    ) { }

  ngOnInit(): void {
    this.initTable();
    this.loadRouteParams();
    this.loadCourses();
    this.loadQuizQuestions();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'excel'
      ]
    });
  }

  private loadRouteParams(): void {
    const queryParams = this.route.snapshot.queryParams as IAdminQuizQuestionsQueryParams;

    this.routeParams = RouteHelper.map(queryParams, {
      courseid: String,
      moduleid: String,
    });

    this.courseID = this.routeParams.courseid || '';
    this.moduleID = this.routeParams.moduleid || '';
  }

  private async loadCourses() {
    const res = await this.coursesService.getAllCourses({ getmodules: true }).toPromise();

    this.courses = res.courses || [];
    this.course  = this.courseID ? this.courses.find(c => c.title === this.courseID) : null;
    this.module  = this.course && this.moduleID ? (this.course.modules as any[])?.find(m => m.moduleID === this.moduleID) : null;

    this.loadTable();
  }

  private async loadQuizQuestions() {
    if (this.courseID && this.moduleID) {
      const res: any = await this.quizService.getModuleQuizzes(this.moduleID).toPromise();
      this.questions = res.docs || [];
      this.loadTable();
    }
    else {
      this.questions = [];
      this.loadTable();
    }
  }

  private async tryReloadTable() {
    await this.loadQuizQuestions().catch(() => {});
  }

  private async loadTable() {
    if (!this.dtElement || !this.questions) {
      return;
    }

    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  // Navigate to the course clicked on
  onCourse(course: any) {
    this.course = course;
    this.courseID = course.title;
    this.open(this.courseID, '');
  }

  // Navigate to the module clicked on
  onCourseModule(module: any) {
    this.module = module;
    this.moduleID = module.moduleID;
    this.open(this.courseID, this.moduleID);
    this.loadQuizQuestions();
  }

  async open(courseID: string, moduleID: string) {
    this.courseID = courseID;
    this.moduleID = moduleID;

    if (!courseID) {
      this.course = null;
    }

    if (!moduleID) {
      this.module = null;
    }

    const queryParams = {
      courseid: courseID || null,
      moduleid: moduleID || null,
    };

    await this.router.navigate(['.'], { replaceUrl: true, queryParams, relativeTo: this.route });
  }

  courseName(course: any) {
    return this.coursesService.getName(course);
  }

  async deleteQuestion(index: number) {
    const question = this.questions![index];

    if (confirm(`Are you sure you want to permanently delete this question?`) === false) {
      return;
    }

    // Delete User
    await this.adminService.deleteQuizQuestion(question._id).toPromise();

    // Reload table data
    await this.loadQuizQuestions().catch(() => {});
  }

  addQuestion() {
    const dialogConfig = new MatDialogConfig();
    const data: AdminQuizQuestionDialogData = {
      title: 'Add Quiz Question',
    };

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    // Open dialog
    const dialogRef = this.dialog.open(AdminQuizQuestionDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (question: any) => {
      if (!question) return;

      this.routeService.scrollToTop();

      // Add Quiz Question
      const res: any = await this.adminService.addQuizQuestion(this.moduleID, question).toPromise();

      // Rerender manually
      await this.tryReloadTable();

      // Success
      if (res.success) {
        // Display success
        this.flashMessage.show(res.message || "Quiz question added.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  editQuestion(index: number){
    const question = this.questions![index];

    const dialogConfig = new MatDialogConfig();
    const data: AdminQuizQuestionDialogData = {
      title: 'Edit Quiz Question',
      edit:  question
    };

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    // Open dialog
    const dialogRef = this.dialog.open(AdminQuizQuestionDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (data: any) => {
      if (!data) return;

      this.routeService.scrollToTop();

      // Update Quiz Question
      const res: any = await this.adminService.updateQuizQuestion(question._id, data).toPromise();

      // Rerender manually
      await this.tryReloadTable();

      // Success
      if (res.success) {
        // Display success
        this.flashMessage.show(res.message || "Quiz question updated.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }
}
